import api from '~/plugins/apiClient';
import { formatApiUrl } from '~/plugins/apiClient/utils';
import { ApiHandler, ApiHandlerWithData, ApiResponseContext } from '~/plugins/apiClient/_types';
import { AuthUser, AuthUserSchema } from '~/plugins/auth/_types';
import { transformZodSchemaFn } from '~/plugins/zod/utils';
import {
    AuthLoginFormData,
    AuthLoginResponse,
    AuthLoginResponseSchema,
    ChangePasswordFormData,
    ForgotPasswordFormData,
    ResetPasswordFormData,
    ValidateTokenResponse,
    ValidateTokenResponseSchema,
} from './_types';
import { Locale } from '~/plugins/i18n/_types';

const apiEndpointPrefix = 'auth';

export const loginRequest: ApiHandlerWithData<
    AuthLoginFormData,
    ApiResponseContext<AuthLoginResponse, AuthLoginFormData>
> = async (values) => {
    const { activeLocale } = useAppLocale();
    const url = formatApiUrl(apiEndpointPrefix, 'login');
    return api.post(url, values, {
        transform: transformZodSchemaFn(AuthLoginResponseSchema, url),
        params: { lang: activeLocale.value },
    });
};

export const logoutRequest: ApiHandler = async () => {
    return api.get(formatApiUrl(apiEndpointPrefix, 'logout'));
};

export const fetchUserRequest: ApiHandler<ApiResponseContext<AuthUser>> = async () => {
    const url = formatApiUrl(apiEndpointPrefix, 'info');
    return api.get(url, { transform: transformZodSchemaFn(AuthUserSchema, url) });
};

export const forgotPasswordRequest: ApiHandlerWithData<
    ForgotPasswordFormData,
    ApiResponseContext<undefined, ForgotPasswordFormData>
> = async (values) => {
    const { activeLocale } = useAppLocale();
    const url = formatApiUrl(apiEndpointPrefix, 'request-reset-password');
    return api.post(url, values, { params: { lang: activeLocale.value } });
};

export const resetPasswordRequest: ApiHandlerWithData<
    { token: string; data: ResetPasswordFormData },
    ApiResponseContext<undefined, ResetPasswordFormData>
> = async ({ token, data }) => {
    const { activeLocale } = useAppLocale();
    const url = formatApiUrl(apiEndpointPrefix, 'recover-password');
    return api.post(url, { Token: token, Password: data.Password }, { params: { lang: activeLocale.value } });
};

export const changePasswordRequest: ApiHandlerWithData<
    ChangePasswordFormData,
    ApiResponseContext<undefined, ChangePasswordFormData>
> = async (values) => {
    const url = formatApiUrl(apiEndpointPrefix, 'change-password');
    return api.post(url, values);
};

export const validateResetPasswordTokenRequest: ApiHandlerWithData<
    string,
    ApiResponseContext<ValidateTokenResponse>
> = async (token) => {
    const url = formatApiUrl('', `reset-password/validate-token/${token}`);
    return api.get(url, { transform: transformZodSchemaFn(ValidateTokenResponseSchema, url) });
};

export const updateLocaleRequest: ApiHandlerWithData<Locale, ApiResponseContext<AuthUser>> = async (locale) => {
    const url = formatApiUrl(apiEndpointPrefix, 'locale');
    return api.put(url, { Locale: locale }, { transform: transformZodSchemaFn(AuthUserSchema, url) });
};
